/**
 * Flattens a nested object into a single-level object with dot notation keys.
 *
 * e.g. { a: { b: { c: 'd' } } } -> { 'a.b.c': 'd' }
 *
 * Key behaviors:
 * - Arrays are flattened with index notation by default (e.g., 'items.0', 'items.1')
 * - Use safe: true to preserve arrays as-is instead of flattening them
 * - Empty arrays are removed from output when flattened (no keys generated)
 * - Null/undefined inputs return empty object {}
 * - Uses dot (.) as delimiter for all keys
 * - Supports maxDepth option to limit flattening depth
 */
export function flatten(
  obj: Record<string, unknown>,
  options: {
    /** Maximum depth to flatten (default: Infinity) */
    maxDepth?: number
    /** Whether to preserve arrays as-is instead of flattening them (default: false) */
    safe?: boolean
  } = {},
): Record<string, unknown> {
  // Handle null or undefined input
  if (obj == null) {
    return {}
  }

  const { maxDepth = Infinity, safe = false } = options
  const result: Record<string, unknown> = {}

  function recurse(current: unknown, path: string[], depth: number): void {
    // Stop if max depth reached
    if (depth > maxDepth) {
      const key = path.join('.')
      result[key] = current
      return
    }

    // Handle null and undefined
    if (current == null) {
      const key = path.join('.')
      result[key] = current
      return
    }

    // Handle arrays
    if (Array.isArray(current)) {
      if (safe) {
        const key = path.join('.')
        result[key] = current
      } else {
        current.forEach((item, index) => {
          recurse(item, [...path, String(index)], depth + 1)
        })
      }
      return
    }

    // Handle Date objects — serialize to ISO string before they hit the generic object path
    if (current instanceof Date) {
      const key = path.join('.')
      result[key] = current.toISOString()
      return
    }

    // Handle objects
    if (typeof current === 'object') {
      const entries = Object.entries(current)

      // Empty object case
      if (entries.length === 0) {
        const key = path.join('.')
        result[key] = current
        return
      }

      // Recurse into object properties
      entries.forEach(([key, value]) => {
        recurse(value, [...path, key], depth + 1)
      })
      return
    }

    // Primitive values
    const key = path.join('.')
    result[key] = current
  }

  Object.entries(obj).forEach(([key, value]) => {
    recurse(value, [key], 1)
  })

  return result
}
